import { useEffect, useState } from 'react';
import { UserPlus, KeyRound, Pencil, UserX, UserCheck, Lock, Trash2 } from 'lucide-react';
import { useQueue } from '../../context/QueueContext';
import { loadUsers, initUsers, useUsersTick, createUser, updateUser, resetPassword, deleteUser, roleLabel } from '../../data/users';

const EMPTY_FORM = { name: '', login: '', role: 'vendedor', password: '', confirm: '', active: true };

const inputCls =
  'mt-1.5 h-12 w-full rounded-lg border border-input bg-white px-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15';

export default function AdminUsers() {
  const { user } = useQueue();
  const usersTick = useUsersTick();
  const [users, setUsers] = useState(loadUsers);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [resetValue, setResetValue] = useState({ password: '', confirm: '' });
  const [confirmOff, setConfirmOff] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [delError, setDelError] = useState('');

  useEffect(() => {
    initUsers().then(() => setUsers([...loadUsers()]));
  }, []);
  useEffect(() => {
    setUsers([...loadUsers()]);
  }, [usersTick]);

  if (user.role !== 'admin') {
    return (
      <div className="py-16 text-center" data-testid="users-admin-only">
        <Lock className="mx-auto h-10 w-10 text-navy/40" strokeWidth={1.5} />
        <p className="mt-4 font-serif text-2xl text-navy">Acesso restrito a administradores</p>
      </div>
    );
  }

  const refresh = () => setUsers([...loadUsers()]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (form.password !== form.confirm) {
      setFormError('As senhas não conferem.');
      return;
    }
    const res = await createUser(form);
    if (!res.ok) {
      setFormError(res.reason === 'login_existe' ? 'Este login já está em uso.' : 'Preencha nome, login e senha.');
      return;
    }
    setForm(EMPTY_FORM);
    setFormOpen(false);
    refresh();
  };

  const saveEdit = async () => {
    try {
      await updateUser(editing.id, { name: editing.name, role: editing.role });
    } catch {}
    setEditing(null);
    refresh();
  };

  const saveReset = async () => {
    if (!resetValue.password || resetValue.password !== resetValue.confirm) return;
    try {
      await resetPassword(resetting.id, resetValue.password);
    } catch {}
    setResetting(null);
    setResetValue({ password: '', confirm: '' });
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-navy">Usuários e Vendedores</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Senhas nunca são exibidas — o administrador pode apenas definir uma nova senha. As senhas são
            armazenadas com hash seguro (bcrypt) no servidor.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen((o) => !o)}
          data-testid="users-new-button"
          className="flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
        >
          <UserPlus className="h-4 w-4" /> NOVO USUÁRIO
        </button>
      </div>

      {formOpen && (
        <form onSubmit={submitCreate} className="mt-5 rounded-2xl border border-navy/10 bg-white p-6" data-testid="users-create-form" noValidate>
          <p className="font-serif text-lg text-navy">Novo usuário</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="nu-name" className="text-sm font-semibold text-navy">Nome completo</label>
              <input id="nu-name" data-testid="users-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label htmlFor="nu-login" className="text-sm font-semibold text-navy">Usuário/Login</label>
              <input id="nu-login" data-testid="users-login-input" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} autoCapitalize="none" className={inputCls} />
            </div>
            <div>
              <label htmlFor="nu-role" className="text-sm font-semibold text-navy">Perfil</label>
              <select id="nu-role" data-testid="users-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                <option value="vendedor">Vendedor</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div>
              <label htmlFor="nu-active" className="text-sm font-semibold text-navy">Status</label>
              <select id="nu-active" data-testid="users-active-select" value={form.active ? 'ativo' : 'inativo'} onChange={(e) => setForm({ ...form, active: e.target.value === 'ativo' })} className={inputCls}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>
            <div>
              <label htmlFor="nu-password" className="text-sm font-semibold text-navy">Senha inicial</label>
              <input id="nu-password" data-testid="users-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" className={inputCls} />
            </div>
            <div>
              <label htmlFor="nu-confirm" className="text-sm font-semibold text-navy">Confirmar senha</label>
              <input id="nu-confirm" data-testid="users-confirm-input" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} autoComplete="new-password" className={inputCls} />
            </div>
          </div>
          {formError && <p className="mt-3 text-sm font-medium text-red-600" role="alert" data-testid="users-form-error">{formError}</p>}
          <div className="mt-5 flex gap-3">
            <button type="submit" data-testid="users-create-submit" className="rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-hover">
              CRIAR USUÁRIO
            </button>
            <button type="button" onClick={() => setFormOpen(false)} className="rounded-full border border-navy/20 px-7 py-3 text-sm font-semibold text-navy hover:bg-navy-light" data-testid="users-create-cancel">
              CANCELAR
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 space-y-3" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} className="rounded-2xl border border-navy/10 bg-white p-5" data-testid={`user-card-${u.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium">
                  {u.name}
                  <span className="ml-2 text-sm text-muted-foreground">@{u.login}</span>
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{roleLabel(u.role)}</p>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${u.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}
                data-testid={`user-status-${u.id}`}
              >
                {u.active ? 'Ativo' : 'Inativo'}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={() => { setEditing({ ...u }); setResetting(null); }} data-testid={`user-edit-${u.id}`} className="flex min-h-11 items-center gap-1.5 rounded-full border border-navy/20 px-4 py-2 text-xs font-semibold text-navy hover:bg-navy-light">
                <Pencil className="h-3.5 w-3.5" /> EDITAR
              </button>
              <button type="button" onClick={() => { setResetting(u); setEditing(null); setResetValue({ password: '', confirm: '' }); }} data-testid={`user-reset-${u.id}`} className="flex min-h-11 items-center gap-1.5 rounded-full border border-navy/20 px-4 py-2 text-xs font-semibold text-navy hover:bg-navy-light">
                <KeyRound className="h-3.5 w-3.5" /> REDEFINIR SENHA
              </button>
              {u.id !== user.id && (
                <button
                  type="button"
                  onClick={async () => {
                    if (u.active) {
                      setConfirmOff(u);
                    } else {
                      try {
                        await updateUser(u.id, { active: true });
                      } catch {}
                      refresh();
                    }
                  }}
                  data-testid={`user-toggle-active-${u.id}`}
                  className={`flex min-h-11 items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold ${u.active ? 'border-red-300 text-red-700 hover:bg-red-50' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                >
                  {u.active ? <><UserX className="h-3.5 w-3.5" /> DESATIVAR</> : <><UserCheck className="h-3.5 w-3.5" /> REATIVAR</>}
                </button>
              )}
              {u.id !== user.id && (
                <button
                  type="button"
                  onClick={() => { setConfirmDel(u); setDelError(''); }}
                  data-testid={`user-delete-${u.id}`}
                  className="flex min-h-11 items-center gap-1.5 rounded-full border border-red-300 px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> EXCLUIR
                </button>
              )}
            </div>

            {editing?.id === u.id && (
              <div className="mt-4 rounded-xl bg-navy-light/60 p-4" data-testid={`user-edit-form-${u.id}`}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor={`edit-name-${u.id}`} className="text-xs font-semibold text-navy">Nome</label>
                    <input id={`edit-name-${u.id}`} data-testid={`user-edit-name-${u.id}`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor={`edit-role-${u.id}`} className="text-xs font-semibold text-navy">Perfil</label>
                    <select id={`edit-role-${u.id}`} data-testid={`user-edit-role-${u.id}`} value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className={inputCls}>
                      <option value="vendedor">Vendedor</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={saveEdit} data-testid={`user-edit-save-${u.id}`} className="rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy-hover">SALVAR</button>
                  <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-navy/20 px-5 py-2.5 text-xs font-semibold text-navy">CANCELAR</button>
                </div>
              </div>
            )}

            {resetting?.id === u.id && (
              <div className="mt-4 rounded-xl bg-navy-light/60 p-4" data-testid={`user-reset-form-${u.id}`}>
                <p className="text-xs font-semibold text-navy">Definir nova senha para {u.name} (a senha atual nunca é exibida)</p>
                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                  <input type="password" placeholder="Nova senha" aria-label="Nova senha" data-testid={`user-reset-password-${u.id}`} value={resetValue.password} onChange={(e) => setResetValue({ ...resetValue, password: e.target.value })} autoComplete="new-password" className={inputCls} />
                  <input type="password" placeholder="Confirmar nova senha" aria-label="Confirmar nova senha" data-testid={`user-reset-confirm-${u.id}`} value={resetValue.confirm} onChange={(e) => setResetValue({ ...resetValue, confirm: e.target.value })} autoComplete="new-password" className={inputCls} />
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={saveReset}
                    disabled={!resetValue.password || resetValue.password !== resetValue.confirm}
                    data-testid={`user-reset-save-${u.id}`}
                    className="rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy-hover disabled:opacity-40"
                  >
                    SALVAR NOVA SENHA
                  </button>
                  <button type="button" onClick={() => setResetting(null)} className="rounded-full border border-navy/20 px-5 py-2.5 text-xs font-semibold text-navy">CANCELAR</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {confirmOff && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-4 sm:items-center" role="dialog" aria-modal="true" data-testid="user-deactivate-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <p className="font-serif text-lg text-navy">Desativar {confirmOff.name}?</p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              O login será bloqueado imediatamente. O histórico de atendimentos e alterações deste
              usuário será preservado — nada é apagado.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setConfirmOff(null)} data-testid="user-deactivate-cancel" className="rounded-full border border-navy/20 py-3 text-sm font-semibold text-navy hover:bg-navy-light">CANCELAR</button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await updateUser(confirmOff.id, { active: false });
                  } catch {}
                  setConfirmOff(null);
                  refresh();
                }}
                data-testid="user-deactivate-confirm"
                className="rounded-full bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700"
              >
                CONFIRMAR
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDel && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-4 sm:items-center" role="dialog" aria-modal="true" data-testid="user-delete-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <p className="font-serif text-lg text-navy">Excluir {confirmDel.name} definitivamente?</p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              O cadastro será apagado do servidor e <strong>não poderá ser recuperado</strong> — o login deixa de
              funcionar imediatamente. O histórico de atendimentos e alterações feitas por {confirmDel.name} será
              preservado com o nome dele(a). Para afastamento temporário, prefira DESATIVAR.
            </p>
            {delError && <p className="mt-3 text-sm font-medium text-red-600" role="alert" data-testid="user-delete-error">{delError}</p>}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setConfirmDel(null)} data-testid="user-delete-cancel" className="rounded-full border border-navy/20 py-3 text-sm font-semibold text-navy hover:bg-navy-light">CANCELAR</button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await deleteUser(confirmDel.id);
                    setConfirmDel(null);
                    refresh();
                  } catch (e) {
                    setDelError(e.message || 'Falha ao excluir o usuário.');
                  }
                }}
                data-testid="user-delete-confirm"
                className="rounded-full bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700"
              >
                EXCLUIR DEFINITIVAMENTE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
